// =============================================================================
// API
// =============================================================================

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

/**
 * Google reCAPTCHA v3 site key (public). Set REACT_APP_RECAPTCHA_SITE_KEY at build time (Docker/CI)
 * or in .env.local. Empty when unset: no token is sent; fine for local dev if the API allows it.
 */
export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// =============================================================================
// UI (formerly ui.config)
// =============================================================================

const ICON_SIZE_SMALL = 18;

const COLORS = {
  unavailable: '#64748b',
  default: '#64748b'
};

const BADGE_STYLES = {
  base: {
    borderRadius: '12px',
    padding: '6px 14px',
    fontWeight: 600,
    display: 'inline-block',
    textAlign: 'center',
    color: '#fff'
  },
  compact: {
    borderRadius: '8px',
    padding: '4px 10px',
    fontWeight: 500,
    fontSize: '12px'
  },
  minWidth: {
    standard: 120,
    compact: 80
  }
};

export const UI_CONSTANTS = {
  UNAVAILABLE_LABEL: 'Unavailable',
  UNAVAILABLE_COLOR: COLORS.unavailable,
  DEFAULT_COLOR: COLORS.default,
  ICON_SIZE_SMALL,
  BADGE_STYLES: BADGE_STYLES.base,
  BADGE_MIN_WIDTH: BADGE_STYLES.minWidth
};

// =============================================================================
// Application-wide constants
// =============================================================================

export const APP_CONFIG = {
  PAGE_SIZE: 25,
  REFRESH_INTERVAL_MS: 5 * 60 * 1000, // 5 minutes
};

// Default columns always requested for entity data
export const BASE_COLUMNS = [
  'EntityId',
  'IsRemoved',
  'Source',
  'ProfileName',
  'AntisemitismTypes',
  'Language',
  'CreatedDate',
  'DisplayName',
];

// Extra columns requested when the user expands filters on the /entities screen
export const ADDITIONAL_COLUMNS = [
  'Subjects',
  'PolicyViolation',
  'AntisemitismTypes',
  'PolicyViolationTheme',
  'PolicyViolationDescription',
  'PostType',
  'totalLikesCount',
  'totalCommentsCount',
  'totalSharesCount',
  'totalRetweetCount',
  'totalViewsCount',
  'totalBookmarksCount',
];

// =============================================================================
// Antisemitism type reference (landing / education)
// =============================================================================

export const ANTIS_EMITISM_TYPES = [
  {
    id: '1',
    name: 'Calling for Harm Against Jews',
    description: 'Calling for, aiding, justifying, or celebrating violence against Jewish people',
  },
  {
    id: '2',
    name: 'Demonization and Conspiracy Myths',
    description: 'Dehumanizing, demonizing, or conspiratorial claims about Jewish control or influence',
  },
  {
    id: '3',
    name: 'Scapegoating Jews',
    description: 'Blaming Jews collectively for real or imagined wrongdoing by Jews or non-Jews',
  },
  {
    id: '4',
    name: 'Holocaust Denial/Distortion',
    description: 'Denying, distorting, or minimizing the fact, scope, or intent of the Holocaust',
  },
  {
    id: '5',
    name: 'Inventing or Exaggerating the Holocaust',
    description: 'Claiming Jews or Israel invented, orchestrated, or exaggerated the Holocaust',
  },
  {
    id: '6',
    name: 'Dual Loyalty Accusations',
    description: 'Accusing Jews of greater loyalty to Israel or global Jewish interests than their country',
  },
  {
    id: '7',
    name: 'Denial of Self-Determination',
    description: 'Denying the Jewish people their right to self-determination or national existence',
  },
  {
    id: '8',
    name: 'Double Standards Toward Israel',
    description: 'Applying standards to Israel not expected of any other democratic nation',
  },
  {
    id: '9',
    name: 'Classic Antisemitic Tropes About Israel or Israelis',
    description: 'Using the symbols and images associated with classic antisemitism to characterize Israel or Israelis',
  },
  {
    id: '10',
    name: 'Israel-Nazi Comparisons',
    description: 'Comparing contemporary Israeli policy or actions to Nazi Germany',
  },
  {
    id: '11',
    name: 'Collective Jewish Responsibility',
    description: 'Holding Jews collectively responsible for actions of the state of Israel',
  },
  {
    id: '12',
    name: 'Targeting Jewish Lifestyle',
    description: 'Targeting Jewish practice through boycotts or bans on kosher products or circumcision',
  },
  {
    id: '13',
    name: 'Jews and Zionists as Synonyms',
    description: 'Using “Jews” and “Zionists” interchangeably',
  },
  {
    id: 'CW1',
    name: 'Denial of Violence Against Jews',
    description: 'Denying or minimizing antisemitic attacks against Jews',
  },
  {
    id: 'CW2',
    name: 'Denial of Violence Against Israelis',
    description: 'Denying or minimizing antisemitic attacks against Israelis',
  },
  {
    id: 'CW3',
    name: 'Conspiratorial Self-Victimization About Jews',
    description: 'Claiming Jews were responsible for violent attacks committed against themselves',
  },
  {
    id: 'CW4',
    name: 'Self-Victimization Conspiracy About Israelis',
    description: 'Claiming Israelis were responsible for violent attacks committed against themselves',
  },
];

// =============================================================================
// Supported social media platforms (report form link validation)
// =============================================================================

/**
 * Networks CyberWell ingests content from, with the registrable domains that
 * belong to each. Mirrors PLATFORM_DOMAINS in cw_proxy — the gateway remains
 * the authority; this list only lets the report form fail fast before a request
 * is sent. Subdomains (www., m., web.) are handled by the validators in
 * `src/utils/urlValidation.js`, so adding a network means adding an entry here
 * and nothing else.
 */
export const SUPPORTED_PLATFORMS = [
  { name: 'Facebook', domains: ['facebook.com'] },
  { name: 'Twitter', domains: ['twitter.com', 'x.com'] },
  { name: 'Instagram', domains: ['instagram.com'] },
  { name: 'YouTube', domains: ['youtube.com', 'youtu.be'] },
  { name: 'TikTok', domains: ['tiktok.com'] },
];
